import React, { Fragment, useState, useEffect, useRef } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  searchValue,
  buttonClass,
  buttonName,
  isAddButton,
  isLoading,
  isCustomPageSize = false,
  handleUserClick,
  page,
  countPerPage,
  totalRecords,
  sortingState,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onSearch,
}) => {
  const [localSearch, setLocalSearch] = useState(searchValue ?? "");
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocalSearch(searchValue ?? "");
  }, [searchValue]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleSearchChange = (value) => {
    setLocalSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(value), 400);
  };

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.max(1, Math.ceil(totalRecords / countPerPage)),
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: countPerPage,
      },
      sorting: sortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sortingState) : updater;

      let nextSort;

      if (!sortingState.length) {
        nextSort = { id: newSorting[0]?.id, desc: false };
      } else if (!sortingState[0].desc) {
        nextSort = { id: sortingState[0].id, desc: true };
      } else {
        nextSort = { id: newSorting[0]?.id, desc: false };
      }

      if (nextSort) {
        onSortChange(nextSort.id, nextSort.desc);
      } else {
        onSortChange(nextSort.id, nextSort.desc);
      }
    },
  });

  const totalPages = Math.max(1, table.getPageCount());
  const maxVisiblePages = 5;

  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const start = (page - 1) * countPerPage + 1;
  const end = Math.min(page * countPerPage, totalRecords);

  return (
    <Fragment>

      <Row className="mb-2">

        {isCustomPageSize && (
          <Col sm={1}>
            <select
              className="form-select pageSize mb-2"
              value={countPerPage}
              onChange={e => onPageSizeChange(Number(e.target.value))}
            >
              {[100, 200, 300, 400, 500].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <Col sm={4}>
            <input
              className="form-control search-box me-2 mb-2 d-inline-block"
              placeholder={SearchPlaceholder}
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Col>
        )}

        {isAddButton && (
          <Col sm={6}>
            <div className="text-sm-end">
              <Button type="button" className={buttonClass} onClick={handleUserClick}>
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      {/* 📊 TABLE (UNCHANGED UI) */}
      <div className={divClassName || "table-responsive"} style={{ maxHeight: "500px", overflowY: "auto" }}>
        <Table hover className={tableClass} bordered={isBordered} style={{ borderCollapse: "separate", borderSpacing: 0, border: "1px solid #d4d4d4" }}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className={header.column.getCanSort() ? "sorting" : ""}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      ...(header.column.id === "Student_Name"
                        ? {
                          width: "125px",
                          minWidth: "125px",
                          maxWidth: "125px",
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                        }
                        : {}),

                      cursor: header.column.getCanSort() ? "pointer" : "default",
                      position: "sticky",
                      top: 0,
                      left: header.column.columnDef.meta?.sticky ? 0 : undefined,
                      zIndex: header.column.columnDef.meta?.sticky ? 3 : 1,
                      background: header.column.columnDef.meta?.sticky ? "#fff" : undefined,
                      border: 0,
                      borderRight: "1px solid #d4d4d4",
                      borderBottom: "1px solid #d4d4d4",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {/* Sorting Icons */}
                    {header.column.getCanSort() && (
                      <span className="ms-2">
                        {header.column.getIsSorted() === "asc" && (
                          <i className="fas fa-sort-amount-up"></i>
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <i className="fas fa-sort-amount-down"></i>
                        )}
                        {!header.column.getIsSorted() && (
                          <i className="fas fa-sort-amount-down"></i>
                        )}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No records found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        ...(cell.column.id === "Student_Name"
                          ? {
                            width: "125px",
                            minWidth: "125px",
                            maxWidth: "125px",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }
                          : {}),

                        position: cell.column.columnDef.meta?.sticky ? "sticky" : "static",
                        left: cell.column.columnDef.meta?.sticky ? 0 : undefined,
                        zIndex: cell.column.columnDef.meta?.sticky ? 2 : 1,
                        background: cell.column.columnDef.meta?.sticky ? "#fff" : undefined,
                        border: 0,
                        borderRight: "1px solid #d4d4d4",
                        borderBottom: "1px solid #d4d4d4",
                      }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* 📄 PAGINATION (UNCHANGED UI) */}
      {isPagination && totalRecords > 0 && (
        <Row className="justify-content-md-end justify-content-center align-items-center">
          <Col>
            <div className="dataTables_info">
              Showing {start} - {end} of {totalRecords} Results
            </div>
          </Col>

          <Col className="col-md-auto">
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li className={`paginate_button page-item previous ${page === 1 ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={() => onPageChange(page - 1)}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {startPage > 1 && (
                  <>
                    <li className="paginate_button page-item">
                      <Link to="#" className="page-link" onClick={() => onPageChange(1)}>1</Link>
                    </li>
                    {startPage > 2 && (
                      <li className="paginate_button page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    )}
                  </>
                )}

                {pages.map((pageNumber) => (
                  <li
                    key={pageNumber}
                    className={`paginate_button page-item ${page === pageNumber ? "active" : ""}`}
                  >
                    <Link to="#" className="page-link" onClick={() => onPageChange(pageNumber)}>
                      {pageNumber}
                    </Link>
                  </li>
                ))}

                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && (
                      <li className="paginate_button page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    )}
                    <li className="paginate_button page-item">
                      <Link to="#" className="page-link" onClick={() => onPageChange(totalPages)}>
                        {totalPages}
                      </Link>
                    </li>
                  </>
                )}

                <li className={`paginate_button page-item next ${page >= table.getPageCount() ? "disabled" : ""}`}>
                  <Link to="#" className="page-link" onClick={() => onPageChange(page + 1)}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
